import { useFrame } from '@react-three/fiber'
import TWEEN from '@tweenjs/tween.js'
import { Object3D, Vector2, Vector3 } from 'three'
import { GLTFActions } from '../../f.assets/animations/CharactersAnimations'
import { TICK_TIME_MS } from '../BattleController/BattleController'
import { TURN_STATES } from '../../c.managers/LevelManager/store.d'
import { dispatch, EVENTS_OBJECTS } from '../../b.providers/EventsProvider/EventsProvider'
import { useSceneStore } from '../../a.scenes/GameScene/store'
import useCappedFrame from '../../utils/hooks/useCappedFrames'
import useTruthyEffect from '../../utils/hooks/useTruthyEffect'
import { coordsToPosition, positionToCoords } from '../../e.elements/Grid/scripts/helpers/coordsPositionConverter'
import getElementByUUID from '../../e.elements/Grid/scripts/helpers/getElementByUUID'
import getGameElementFromObj from '../../e.elements/GameElements/helpers/getGameElementFromObj'
import Minion from '../../e.elements/GameElements/classes/Minion'
import { GameObject3D } from '../../e.elements/GameElements/classes/types'
import { Terrain } from '../TerrainController'
import {
  CARDINAL_POINT,
  getCoordsVectorFromCoords,
  lookAtCardinalPoint,
} from '../../f.assets/scripts/lookAtCardinalPoint'
import GameObject from '../../e.elements/GameElements/classes/GameObject'
import Character from '../../e.elements/GameElements/classes/Character'

export const API = {
  simpleTravel: async (obj: Object3D, origin: Vector2, target: Vector2) => {
    const character = getGameElementFromObj(obj as GameObject3D) as Character
    const animations = useSceneStore.getState().getGameElementsAnimationActions()

    const elementActions = animations.get(obj.uuid) as GLTFActions
    // const gridOrigin = useSceneStore.getState().terrain.data.gridOrigin
    const updateCharacterState = useSceneStore.getState().characters.updateCharacterState
    const coords = origin
    const isLast = false
    const DURATION = TICK_TIME_MS / obj.userData.tickDivisions.length // make it relative to the minion to apply different speeds

    // obj.position.copy(coordsToPosition(origin, gridOrigin))
    // @TODO REVIEW
    updateCharacterState(obj.uuid, {
      coords: target,
      position: obj.position, // coordsToPosition(target, gridOrigin),
    })

    if (coords.x > target.x) {
      await character.animator.moveLeft(elementActions, isLast, DURATION)
    } else if (coords.x < target.x) {
      await character.animator.moveRight(elementActions, isLast, DURATION)
    } else if (coords.y > target.y) {
      await character.animator.moveUp(elementActions, isLast, DURATION)
    } else if (coords.y < target.y) {
      await character.animator.moveDown(elementActions, isLast, DURATION)
    }
  },
  attack: async (obj: GameObject3D, targetObj: GameObject3D) => {
    const character = getGameElementFromObj(obj) as Character
    const characterTarget = getGameElementFromObj(targetObj) as Character

    await API.faceTowards(obj, characterTarget.coords)

    character.animator.playAttack()
  },
  defeat: async (obj: GameObject3D) => {
    const character = getGameElementFromObj(obj) as Character
    await character.animator.playDefeat()
  },
  celebrate: async (obj: GameObject3D) => {
    const character = getGameElementFromObj(obj) as Character

    character.animator.playCheer()
  },
  idle: async (obj: GameObject3D) => {
    const character = getGameElementFromObj(obj) as Character

    character.animator.idle()
  },
  loot: async (obj: GameObject3D, target: Vector2) => {
    const character = getGameElementFromObj(obj) as Character
    await API.faceTowards(obj, target)
    // character.animator.lookAtPoint(new Vector3(target.x, 0, target.y))
    character.animator.playLooting()
  },
  pickUpFrom: async (obj: GameObject3D, coords: Vector2) => {
    const character = getGameElementFromObj(obj) as Character
    await API.faceTowards(character.obj, coords)
    await character.animator.playPick()
  },
  faceTowards: async (obj: GameObject3D, target: Vector2) => {
    const character = getGameElementFromObj(obj) as Character
    const coordsVector = getCoordsVectorFromCoords(character.coords, target)
    
    await character.animator.lookAtPoint(new Vector3(coordsVector.x, 0, coordsVector.y))
  },
  riseFromTheGround: async (obj: Object3D) => {
    const DURATION = TICK_TIME_MS / obj.userData.tickDivisions.length // make it relative to the minion to apply different speeds
    const character = getGameElementFromObj(obj as GameObject3D) as Character
    await character.animator.digUp(DURATION)
  },
  dieIntoTheGround: async (obj: GameObject3D) => {
    const minion = getGameElementFromObj(obj) as Minion
    minion.tickDivisions
    await API.defeat(obj)
    await minion.animator.bury(1500)
    // fix mutation after dead
    minion.fixMutation()
    minion.animator.stop()
  },
  // @deprecated this is inherited from tactics dungeons
  travel: async (obj: Object3D, travelPath: Record<'x' | 'y', number>[]) => {
    let [origin, ...path] = travelPath

    if (path.length > 0) {
      const {
        characters: { updateCharacterState },
        terrain: {
          getSelectedState,
          data: { gridOrigin },
        },
      } = useSceneStore.getState()
      const element = obj
      const animations = useSceneStore.getState().getGameElementsAnimationActions()
      // const updateCharacterState = useSceneStore.getState().updateCharacterState
      // const gridOrigin = useSceneStore.getState().gridOrigin
      const elementActions = animations.get(element.uuid) as GLTFActions
      const selectedState = getSelectedState()

      const enqueueMoves = async (movesArrays: Record<'x' | 'y', number>[]) => {
        const lastPosition = movesArrays[movesArrays.length - 1]
        const move = async (coords: Record<'x' | 'y', number>, isLast: boolean) => {
          // animate movement
          if (origin.x > coords.x) {
            // await moveUp(element, elementActions, isLast, 500)
          } else if (origin.x < coords.x) {
            // await moveDown(element, elementActions, isLast, 500)
          } else if (origin.y > coords.y) {
            // await moveLeft(element, elementActions, isLast, 500)
          } else if (origin.y < coords.y) {
            // await moveRight(element, elementActions, isLast, 500)
          }
          origin = coords
        }
        await movesArrays.reduce(async (previousPromise, nextScript, idx) => {
          await previousPromise
          const isLast = idx === movesArrays.length - 1
          return move(nextScript, isLast)
        }, Promise.resolve())

        const lastCoords = new Vector2(lastPosition.x, lastPosition.y)
        updateCharacterState(element.uuid, {
          coords: lastCoords,
          position: coordsToPosition(lastCoords, gridOrigin),
          mv: selectedState.mv - movesArrays.length,
        })

        return true
      }
      await enqueueMoves(path)
    }

    useSceneStore.getState().terrain.unlockInteraction()
    dispatch(EVENTS_OBJECTS.MOVE_END, {})
  },
}
const AnimationController = () => {
  // STATE
  const travelPath = useSceneStore(s => s.terrain.data.travelPath)
  const { setTravelPath, clearSelectedCoords } = useSceneStore(s => s.terrain)
  const setTurnState = useSceneStore(s => s.levels.setTurnState)
  const characterStates = useSceneStore(s => s.characters.data.characterStates)
  const elementUUID = useSceneStore(s => s.terrain.data.selectedElementUUID)

  const clearTravelPath = () => setTravelPath([])

  // sequence to manage the store before and after a character travels
  const triggerMoveActions = async (obj: Object3D) => {
    setTurnState(TURN_STATES.MOVE)
    clearTravelPath()
    await API.travel(obj, travelPath!)
    clearSelectedCoords()
  }

  // effect that triggers char travel
  useTruthyEffect(() => {
    // pre conditions
    const state = characterStates[elementUUID!]
    const obj = getElementByUUID(elementUUID!)

    if (state && obj) {
      triggerMoveActions(obj)
    } else {
      throw new Error(`GAME ENGINE: Couldn't find element with UUID: ${elementUUID}`)
    }
  }, [travelPath, elementUUID, characterStates])

  return null
}

export default AnimationController
